import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

import ChangeUserStatusDialog from '../components/changeUserStatusDialog/ChangeUserStatusDialog';
import AmbraNamespace from '../models/AmbraNamespace';

export default class WorkspacesBloc {
  selectedWorkspace$ = new BehaviorSubject();
  isRequestingCode$ = new BehaviorSubject(false);
  isUpdatingNamespaces$ = new BehaviorSubject(false);
  dispose$ = new Subject();
  ambraNamespaces$ = new BehaviorSubject([]);
  discounts$ = new BehaviorSubject([]);

  constructor({ ambraNamespaceRepository, authRepository, errorRepository, dialogRepository, drawerRepository, workspaceRepository, adminRepository, discountsRepository }) {
    this.drawerRepository = drawerRepository;
    this.workspaceRepository = workspaceRepository;
    this.adminRepository = adminRepository;
    this.ambraNamespaceRepository = ambraNamespaceRepository;
    this.errorRepository = errorRepository;
    this.dialogRepository = dialogRepository;
    this.discountsRepository = discountsRepository;
    this.authRepository = authRepository;

    this.workspaceRepository.searchCriteria$
      .pipe(
        filter(() => !!authRepository.auth$.getValue()),
        tap(() => this.getWorkspaces()),
        takeUntil(this.dispose$)
      )
      .subscribe();

    this.selectedWorkspace$
      .pipe(
        filter(() => !!authRepository.auth$.getValue()),
        filter((e) => e),
        tap((workspace) => {
          this.getAdministrators(this.adminRepository.searchCriteria$.getValue(), workspace.id);
        }),
        takeUntil(this.dispose$)
      )
      .subscribe();

    this.adminRepository.searchCriteria$
      .pipe(
        filter(() => !!authRepository.auth$.getValue()),
        filter(() => !!this.selectedWorkspace$.getValue()),
        tap((criteria) => this.getAdministrators(criteria, this.selectedWorkspace$.getValue().id)),
        takeUntil(this.dispose$)
      )
      .subscribe();
  }

  dispose() {
    this.workspaceRepository.resetSearchCriteria();
    this.adminRepository.resetSearchCriteria();
    this.dispose$.next(true);
  }

  async getWorkspaces(criteria) {
    return this.workspaceRepository.getWorkspaces(criteria);
  }

  async getWorkspaceById(id) {
    const oldWorkspaces = this.workspaceRepository.workSpaces$.getValue();
    const updatedWorkspace = await this.workspaceRepository.getWorkspaceById(id);

    let updatedWorkspaces = oldWorkspaces.data;
    if (updatedWorkspaces && updatedWorkspaces.length > 0) {
      updatedWorkspaces = updatedWorkspaces.map((workspace) => (workspace.id === id ? updatedWorkspace : workspace));
    }

    return updatedWorkspace;
  }

  addAdmin(component) {
    this.drawerRepository.loadDrawer(component);
  }

  resendInvite(admin, component) {
    this.adminRepository.adminToInvite$.next(admin);
    this.drawerRepository.loadDrawer(component);
  }

  getAdministrators(criteria, workspaceId) {
    this.adminRepository.getAdministrators(criteria, workspaceId);
  }

  async getAmbraNamespaces() {
    const results = await this.ambraNamespaceRepository.getAmbraNamespace();
    this.ambraNamespaces$.next(results.map((result) => new AmbraNamespace({ label: result.name, value: result.id })));
  }

  currentWorkspaces() {
    const workspaces = this.workspaceRepository.workSpaces$.getValue();

    return workspaces.data;
  }

  async setWorkspace(workspace) {
    const { id } = workspace;
    if (id) {
      const updatedWorkspace = await this.getWorkspaceById(id);
      this.selectedWorkspace$.next(updatedWorkspace);
      this.getAllDiscounts();
    }
  }

  async getAllDiscounts() {
    const discounts = await this.discountsRepository.getAllDiscounts();
    this.discounts$.next(discounts);
  }

  async updateLinkedNamespaces(workspaceId, namespaceIds) {
    try {
      this.isUpdatingNamespaces$.next(true);
      await this.workspaceRepository.updateNamespaces(workspaceId, namespaceIds);
      await this.updateSelectedWorkspace(workspaceId);
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message || 'Failed to update linked namespaces',
        severity: 'error',
      });
    } finally {
      this.isUpdatingNamespaces$.next(false);
    }
  }

  updateAdminSearchCriteria(criteria) {
    this.adminRepository.updateSearchCriteria(criteria);
  }

  updateSearchCriteria(criteria) {
    this.workspaceRepository.updateSearchCriteria(criteria);
  }

  async requestClinicCode(name) {
    try {
      this.isRequestingCode$.next(true);
      return await this.workspaceRepository.requestClinicCode(name);
    } finally {
      this.isRequestingCode$.next(false);
    }
  }

  async refreshSelectedWorkspace(id) {
    const selectedWorkspace = this.selectedWorkspace$.getValue();
    if (id === selectedWorkspace.id) {
      await this.updateSelectedWorkspace();
    }
  }

  async updateWorkspaceMainFileType(id, mainFileType) {
    await this.workspaceRepository.updateWorkspaceMainFileType(id, mainFileType);
    await this.refreshSelectedWorkspace(id);
  }

  async updateWorkspaceRadiologyReportRequired(id, isRequired) {
    await this.workspaceRepository.updateWorkspaceRadiologyReportRequired(id, isRequired);
    await this.refreshSelectedWorkspace(id);
  }

  async updateWorkspaceSendPlacedOrdersToAdmins(workspaceId, value) {
    await this.workspaceRepository.updateWorkspaceSendPlacedOrdersToAdmins(workspaceId, value);
    await this.refreshSelectedWorkspace(workspaceId);
  }

  async updateWorkspaceEmailsForPlacedOrders(workspaceId, value) {
    await this.workspaceRepository.updateWorkspaceEmailsForPlacedOrders(workspaceId, value);
    await this.refreshSelectedWorkspace(workspaceId);
  }

  async updateCanRequestBeforePay(id, isAllowed) {
    await this.workspaceRepository.updateCanRequestBeforePay(id, isAllowed);
    await this.refreshSelectedWorkspace(id);
  }

  async updateCreatorMode(id, isActive) {
    await this.workspaceRepository.updateCreatorMode(id, isActive);
    await this.refreshSelectedWorkspace(id);
  }

  async updateSelectedWorkspace() {
    const { id } = this.selectedWorkspace$.getValue();
    const updatedWorkspace = await this.getWorkspaceById(id);
    this.setWorkspace(updatedWorkspace);
  }

  get workSpaces$() {
    return this.workspaceRepository.workSpaces$.pipe(
      map((workspaces) => workspaces.data),
      tap((workspaces) => {
        if (!workspaces || !workspaces.length) {
          this.selectedWorkspace$.next(null);
          this.adminRepository.administrators$.next([]);
          return;
        }
        if (this.selectedWorkspace$.getValue()) return;
        this.setWorkspace(workspaces[0]);
      })
    );
  }

  get workspacesCount$() {
    return this.workspaceRepository.workSpaces$.pipe(map((workspaces) => workspaces.total));
  }

  get administrators$() {
    return this.adminRepository.administrators$.pipe(map((admins) => admins.data));
  }

  get user$() {
    return this.authRepository.auth$;
  }

  get adminsCount$() {
    return this.adminRepository.administrators$.pipe(map((admins) => admins.total));
  }

  async disableUser(id) {
    await this.adminRepository.disableAdmin(id);
    await this.getAdministrators(this.adminRepository.searchCriteria$.getValue(), this.selectedWorkspace$.getValue().id);
  }

  async enableUser(id) {
    await this.adminRepository.enableAdmin(id);
    await this.getAdministrators(this.adminRepository.searchCriteria$.getValue(), this.selectedWorkspace$.getValue().id);
  }

  async updateWorkspaceLandingUrl(id, landingUrl) {
    try {
      await this.workspaceRepository.updateLandingUrl(id, landingUrl);
      const selectedWorkspace = this.selectedWorkspace$.getValue();
      if (id === selectedWorkspace.id) {
        await this.updateSelectedWorkspace();
      }
    } catch (e) {
      this.errorRepository.error$.next({
        message: e.message || 'Failed to update landing url',
        severity: 'error',
      });
    }
  }

  async updateWorkspaceSubdomain(id, subdomain) {
    try {
      await this.workspaceRepository.updateSubdomain(id, subdomain);
      const selectedWorkspace = this.selectedWorkspace$.getValue();
      if (id === selectedWorkspace.id) {
        await this.updateSelectedWorkspace();
      }
    } catch (e) {
      this.errorRepository.error$.next({
        message: e.message || 'Failed to update subdomain',
        severity: 'error',
      });
    }
  }

  async updateWorkspaceDiscount(id, discountId) {
    await this.workspaceRepository.updateDiscountId(id, discountId);
    await this.refreshSelectedWorkspace(id);
  }

  openUserRoleChangeDialog({ userId, status }) {
    this.dialogRepository.loadDialog(ChangeUserStatusDialog, {
      onCloseButtonClick: () => this.dialogRepository.toggleDialog(false),
      onDisableUser: (adminId) => {
        this.dialogRepository.toggleDialog(false);
        this.disableUser(adminId);
      },
      onEnableUser: (adminId) => {
        this.dialogRepository.toggleDialog(false);
        this.enableUser(adminId);
      },
      userId,
      userStatus: status,
    });
  }
}
